import _                        from 'underscore';
import classNames               from 'classnames';
import React, { FC, ReactNode } from 'react';
import { StrictCheckboxProps }  from 'semantic-ui-react';
import { useFormikContext }     from 'formik';
import { Cell, Grid }           from 'styled-css-grid';

import { CorvalAuditEntry, CorvalSubObjective, Position } from '@src/types';
import { StyledContainer }                                from '@src/views/containers';
import { Checkbox, FieldDescription }                     from '@src/views/components/forms';
import { AuditMemoList }                                  from '@src/views/components/audits/AuditMemoList';
import { AUDIT_RECORD_TYPE }                              from '@src/constants';
import { CorvalRecommendedTag }                           from '@src/views/components/product/CorvalRecommendedTag';


export interface ObjectiveSelectionLayoutProps {
    item: Partial<CorvalSubObjective>,
    loading?: boolean,
    disabled?: boolean,
    onChange?: StrictCheckboxProps["onChange"],

    /* Info about the user who last updated the objective state */
    auditRecords?: CorvalAuditEntry[],

    /* The fieldname associated with the pin state toggle */
    fieldValueName?: string,

    /* Flag to display as a visually tigher component */
    compact?: boolean,

    /* Flag to toggle last updated view or not */
    showLastUpdated?: boolean,

    /* Additional content react node */
    additionalContent?: ReactNode,
}

const defaultProps: ObjectiveSelectionLayoutProps = {
    item:               null,
    auditRecords:       null,
    loading:            false,
    disabled:           false,
    fieldValueName:     null,
    compact:            false,
    onChange:           () => {},
    showLastUpdated:    true,
};

const ObjectiveSelectionLayout: FC<ObjectiveSelectionLayoutProps> = ( props ) => {

    let formik = useFormikContext();

    let { fieldValueName, additionalContent, item, auditRecords, loading, disabled } = props;

    /* If the toggle is active */
    let isActive = formik.values[ props.fieldValueName ];

    let containerClasses = classNames( {
        [ `padding-xs-vertical` ]: props.compact !== false,
        [ `padding-md-vertical` ]: props.compact === false,
    } );

    return (

        <Grid columns={ `1fr auto` } columnGap={ '50px' } className={ containerClasses }>

            <Cell>

                <h3>{ item.displayName } { props.item.isRecommended && (

                    <StyledContainer className={ 'display--inline-block margin-xs-horizontal' } position={ Position.RELATIVE } top={ 3 }>
                        <CorvalRecommendedTag/>
                    </StyledContainer>

                ) }</h3>

                {/* Description */ }
                { item.description && (

                    <FieldDescription>

                        { item.description }

                    </FieldDescription>
                ) }

                { additionalContent }

                {/* User update information */ }
                { ( !_.isEmpty( auditRecords ) && props.showLastUpdated ) && (

                    <AuditMemoList records={ props.auditRecords }
                                   types={ [ AUDIT_RECORD_TYPE.PROJECT_OBJECTIVE_PINSTATE_CHANGED ] }
                                   timeBoundary={ 'newest' }
                                   actionDesc={ 'made the decision to' }
                                   actionVerb={ isActive ? 'include' : 'exclude' }
                                   auditSubject={ 'this objective' }
                                   auditAdditionalInfo={ ( !isActive && props.item.isRecommended ) ? `Corval recommends you include this objective in your project.` : null }
                                   memoPropsByType={ {} }
                                   wrapperComponent={ ( recordProps ) => {
                                       return <>{ recordProps.children }</>;
                                   } }/>
                ) }

            </Cell>
            <Cell>

                {/* Pin state toggle control */ }
                <Checkbox toggle
                          id={ `bool-${ item.id }` }
                          name={ fieldValueName }
                          disabled={ loading || disabled }
                          onChange={ props.onChange }/>

            </Cell>

        </Grid>
    );
};

ObjectiveSelectionLayout.defaultProps = defaultProps;

export { ObjectiveSelectionLayout }
